import { fabricJsonToSceneGraph } from '../sceneGraph/fabricJsonAdapter';
import { constraintsToFieldConfig } from '../sceneGraph/fieldConfigMigration';
import { normalizeKonvaImageNode, normalizeKonvaSceneImages } from '../api/gcpImagesApi';
import {
  enrichSceneForServerPdf,
  sceneGraphToFabricObjects,
  collectFontsUsedFromScene,
} from '../sceneGraph/serverPdfAdapter';
import { SCENE_GRAPH_VERSION } from '../sceneGraph/schema';

export const KONVA_EDITOR_JSON_VERSION = 2;
export const KONVA_MULTI_PAGE_JSON_VERSION = 3;

/**
 * @typedef {Object} KonvaPageMeta
 * @property {number} width - page width in px
 * @property {number} height - page height in px
 * @property {string} unit
 * @property {number} widthMm
 * @property {number} heightMm
 * @property {number} resolution
 */

export function normalizeEditorJsonInput(json) {
  if (json == null) return null;
  if (typeof json === 'string') {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
  return json;
}

function serializeSceneObjects(objects = []) {
  return objects.map((o) => {
    if (o?.type === 'image') return normalizeKonvaImageNode(o);
    if (o?.type !== 'text') return o;
    const constraints = o.constraints || {};
    return {
      ...o,
      fieldConfig: constraintsToFieldConfig(o.transform, o.typography, constraints, {
        fieldKey: o.fieldKey,
        ...(o.fieldConfig && typeof o.fieldConfig === 'object' ? o.fieldConfig : {}),
      }),
    };
  });
}

/** Serialize Konva/Zustand scene for API storage. */
export function serializeKonvaDocument(scene, meta = {}) {
  return {
    version: KONVA_EDITOR_JSON_VERSION,
    editor: 'konva',
    scene: {
      version: scene?.version ?? SCENE_GRAPH_VERSION,
      width: scene?.width,
      height: scene?.height,
      background: scene?.background,
      objects: serializeSceneObjects(scene?.objects ?? []),
    },
    fontsUsed: collectFontsUsedFromScene(scene),
    background: meta.background || null,
    pageMeta: meta.pageMeta || null,
  };
}

/**
 * Serialize all in-memory pages. Multi-page docs use version 3 with a `pages` array.
 * Single-page docs keep version 2 shape for backward compatibility.
 */
export function serializeKonvaMultiPageDocument(pageDocs) {
  const validPages = (pageDocs || []).filter(Boolean);
  if (!validPages.length) return null;
  if (validPages.length === 1) return validPages[0];

  const first = validPages[0];
  const fontsUsed = [
    ...new Set(validPages.flatMap((p) => p.fontsUsed || collectFontsUsedFromScene(p.scene))),
  ];
  return {
    version: KONVA_MULTI_PAGE_JSON_VERSION,
    editor: 'konva',
    pages: validPages,
    scene: first.scene,
    objects: first.objects || sceneGraphToFabricObjects(first.scene),
    fontsUsed,
    background: first.background,
    pageMeta: first.pageMeta || null,
  };
}

/**
 * Parse saved JSON into an array of per-page editor documents.
 * @returns {{ kind: 'konva'|'fabric'|'empty', pages: object[] }}
 */
export function parseEditorDocumentPages(json) {
  const doc = normalizeEditorJsonInput(json);
  if (!doc) return { kind: 'empty', pages: [] };

  if (
    (doc.editor === 'konva' || doc.version >= KONVA_MULTI_PAGE_JSON_VERSION) &&
    Array.isArray(doc.pages) &&
    doc.pages.length > 0
  ) {
    return {
      kind: 'konva',
      pages: doc.pages.filter(Boolean).map((page) => ({
        ...page,
        scene: page.scene ? normalizeKonvaSceneImages(page.scene) : page.scene,
      })),
    };
  }

  const single = parseEditorDocument(doc);
  if (single.kind === 'konva') {
    return {
      kind: 'konva',
      pages: [
        serializeKonvaDocument(single.scene, {
          background: single.background,
          pageMeta: doc.pageMeta || null,
        }),
      ],
    };
  }
  if (single.kind === 'fabric') {
    return { kind: 'fabric', pages: [single.fabricJson] };
  }
  return { kind: 'empty', pages: [] };
}

/**
 * @returns {{ kind: 'konva'|'fabric'|'empty', scene?: object, fabricJson?: object, background?: object }}
 */
export function parseEditorDocument(json) {
  const doc = normalizeEditorJsonInput(json);
  if (!doc) return { kind: 'empty' };
  if (doc.editor === 'konva' && doc.scene && Array.isArray(doc.scene.objects)) {
    return {
      kind: 'konva',
      scene: normalizeKonvaSceneImages(doc.scene),
      background: doc.background || null,
    };
  }
  if (doc.scene && Array.isArray(doc.scene.objects)) {
    return {
      kind: 'konva',
      scene: normalizeKonvaSceneImages(doc.scene),
      background: doc.background || null,
    };
  }
  if (Array.isArray(doc.objects)) {
    return { kind: 'fabric', fabricJson: doc, background: null };
  }
  return { kind: 'empty' };
}

export function fabricJsonToKonvaScene(fabricJson, pageW, pageH) {
  return fabricJsonToSceneGraph(fabricJson, { width: pageW, height: pageH });
}

export function cloneScene(scene) {
  return JSON.parse(JSON.stringify(scene));
}
